import type { TranslationKeys } from '~/locales/i18next'

interface DeleteScheduleError {
  code?: string
  message?: string
  switchboardNames?: string[]
}

type TranslateFn = (
  key: TranslationKeys,
  defaultValue: string,
  options?: Record<string, unknown>,
) => string

const SCHEDULE_ID_PATTERN =
  '(?:\\d+|[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})'
const DEFAULT_DELETE_MESSAGE_PATTERN = new RegExp(
  `^Failed to delete schedule ${SCHEDULE_ID_PATTERN}$`,
  'i',
)

function normalizeSwitchboardNames(names?: string[]): string[] {
  if (!Array.isArray(names)) return []

  return names.map((name) => name.trim()).filter((name) => name.length > 0)
}

function summarizeSwitchboardNames(
  names: string[],
  t: TranslateFn,
): { summary: string; total: number } {
  if (names.length <= 3) {
    return { summary: names.join(', '), total: names.length }
  }

  const visibleNames = names.slice(0, 3).join(', ')
  const remaining = names.length - 3

  return {
    summary: t(
      'schedule.common.deleteError.listWithMore',
      '{{visibleNames}}, and {{remaining}} more',
      { remaining, visibleNames },
    ),
    total: names.length,
  }
}

export function getLocalizedDeleteScheduleErrorMessage({
  error,
  fallbackMessage,
  t,
}: {
  error: DeleteScheduleError
  fallbackMessage: string
  t: TranslateFn
}): string {
  if (error.code === 'schedule_in_use_by_switchboard') {
    const switchboardNames = normalizeSwitchboardNames(error.switchboardNames)

    if (switchboardNames.length === 0) {
      return t(
        'schedule.common.deleteError.inUseGeneric',
        'This schedule cannot be deleted because it is in use by a switchboard. Remove it from the switchboard and try again.',
      )
    }

    const { summary, total } = summarizeSwitchboardNames(switchboardNames, t)

    if (total === 1) {
      return t(
        'schedule.common.deleteError.inUseSingle',
        'This schedule cannot be deleted because it is used in switchboard: {{switchboardNames}}. Remove it from that switchboard and try again.',
        { switchboardNames: summary },
      )
    }

    return t(
      'schedule.common.deleteError.inUseMultiple',
      'This schedule cannot be deleted because it is used in switchboards: {{switchboardNames}}. Remove it from those switchboards and try again.',
      { switchboardNames: summary },
    )
  }

  if (
    typeof error.message === 'string' &&
    error.message.trim().length > 0 &&
    !DEFAULT_DELETE_MESSAGE_PATTERN.test(error.message)
  ) {
    return error.message
  }

  return fallbackMessage
}
